import { useState } from "react";
import type { Trip, ThemeClasses } from "../types";
import {
  getStatusColor,
  getStatusStyle,
  getStatusLabel,
  getPriorityColor,
  getPriorityLabel,
  formatTripDate,
} from "../utils";
import type { TFunction } from "i18next";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { useOpenChat } from "../../../../../../pages/support/hooks/useOpenChat";
import { activeThemeId } from "../../../../../../config/theme";

const isChatEnabled = activeThemeId === "central-gps";

interface TripGridCardProps {
  trip: Trip;
  themeClasses: ThemeClasses;
  onEdit: (trip: Trip) => void;
  onDetails: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
  onOpenChatModal: (trip: Trip) => void;
  t: TFunction;
  disableCRUD: boolean;
}

export function TripGridCard({
  trip,
  themeClasses,
  onEdit,
  onDetails,
  onDelete,
  onOpenChatModal,
  t,
  disableCRUD,
}: TripGridCardProps) {
  
  const { tryOpenExistingTripChat, isReady } = useOpenChat();
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  const { isMonitor, isReadOnly } = useIsMonitor();

  const assignmentsCount = trip.assignments?.length || 0;
  const activeAssignments =
    trip.assignments?.filter(
      (a) => a.status === "active" || a.status === "pending",
    ).length || 0;

  const handleOpenChat = async () => {
    if (!isReady || isOpeningChat) return;
    setIsOpeningChat(true);
    try {
      const opened = await tryOpenExistingTripChat(trip.id);
      if (!opened) {
        onOpenChatModal(trip);
      }
    } finally {
      setIsOpeningChat(false);
    }
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all`}
    >
      {/* Trip Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center space-x-3 min-w-0">
          <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-route-line text-lg"></i>
          </div>
          <div className="min-w-0">
            <h3 className={`font-semibold ${themeClasses.text} truncate`}>
              {trip.title || t("trips.untitled")}
            </h3>
            <p className={`text-sm ${themeClasses.textSecondary} truncate`}>
              {trip.route?.name || "N/A"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {trip.status?.name && (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trip)}`}
              style={getStatusStyle(trip)}
            >
              {getStatusLabel(trip, t)}
            </span>
          )}
          {isChatEnabled && (
            <button
              type="button"
              onClick={handleOpenChat}
              disabled={!isReady || isOpeningChat}
              title={t("trips.actions.openChat", "Abrir chat del viaje")}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors border ${themeClasses.cardBorder} ${themeClasses.buttonHover} ${themeClasses.buttonText} hover:text-primary-500 ${
                !isReady || isOpeningChat ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <i
                className={`${isOpeningChat ? "ri-loader-4-line animate-spin" : "ri-chat-3-line"} text-sm`}
              ></i>
            </button>
          )}
        </div>
      </div>

      {/* Priority Tag */}
      {trip.priority && (
        <div className="flex items-center space-x-2 mb-4">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(trip.priority)}`}
          >
            {getPriorityLabel(trip.priority, t)}
          </span>
        </div>
      )}

      {/* Trip Details */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("trips.assignments.title")}:
          </span>
          <span
            className={`text-sm ${themeClasses.text} font-medium truncate ml-2`}
          >
            {activeAssignments}/{assignmentsCount}{" "}
            {t("trips.assignments.devices")}
          </span>
        </div>
        {trip.description && (
          <div className="flex items-start justify-between">
            <span className={`text-sm ${themeClasses.textSecondary}`}>
              {t("trips.fields.description")}:
            </span>
            <span
              className={`text-sm ${themeClasses.text} font-medium truncate ml-2 text-right max-w-[60%]`}
            >
              {trip.description}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex space-x-2 mb-3">
        {!isMonitor && !isReadOnly && (
          <>
            <CRUDTooltip className="flex-1">
              <button
                onClick={() => onEdit(trip)}
                className={`w-full py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors`}
                disabled={disableCRUD}
              >
                {t("trips.actions.edit")}
              </button>
            </CRUDTooltip>
            <CRUDTooltip>
              <button
                onClick={() => onDelete(trip)}
                disabled={disableCRUD}
                className="py-2 px-3 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                title={t("trips.actions.delete")}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </CRUDTooltip>
          </>
        )}
        <button
          onClick={() => onDetails(trip)}
          className="py-2 px-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors flex-1"
        >
          {t("trips.actions.details")}
        </button>
      </div>

      {/* Created At */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {t("trips.fields.createdAt")}: {formatTripDate(trip.createdAt)}
        </p>
      </div>
    </div>
  );
}
